import { Stack, Typography } from '@mui/material';
import { SyntheticEvent, useEffect, useMemo } from 'react';
import { useWatch } from 'react-hook-form';

import { Divider } from '@/components/Divider';
import { ButtonGroup } from '@/components/form/baseInputs/ButtonGroup';
import { CheckIcon } from '@/components/icons/CheckIcon';
import { useFormContext } from '@/components/react-hook-form/hooks';
import { COLORS } from '@/styles/tokens/colors';
import { ClientProfileRelationshipType } from '@/types/schema';

import { ClientSetupAdditionalDetails } from './ClientSetup.AdditionalDetails';
import { ClientSetupIndividualIndicator } from './ClientSetup.IndividualIndicator';
import { ClientSetupPrimaryClientForm } from './ClientSetup.PrimaryClientForm';
import { ClientSetupFormShape, DefaultDeathOrder } from './ClientSetup.type';

/**
 * Body for the "Basic info" step: primary/secondary client details, the
 * default death order, and additional details.
 */
export function ClientSetupBody() {
  const { setValue, control, formState } =
    useFormContext<ClientSetupFormShape>();

  const [clientOneValues, clientTwoValues, defaultDeathOrder] = useWatch({
    control,
    name: ['clientOne', 'clientTwo', 'defaultDeathOrder'],
  });

  const isRelationshipFieldDirty =
    formState.dirtyFields.relationshipToBetweenPrimaryClients;

  const isClientTwoStateCodeDirty = Boolean(
    formState.dirtyFields.clientTwo?.stateCode
  );

  // Client two fields become required if any of them have been filled out.
  const clientTwoFieldsFilled = Boolean(
    clientTwoValues?.firstName || clientTwoValues?.lastName
  );

  const isDeathOrderToggleDisabled =
    !clientTwoFieldsFilled || Boolean(clientTwoValues?.isDeceased);

  const deathOrderOptions = useMemo(
    () => [
      {
        display: 'Primary individual',
        value: 'primary' as DefaultDeathOrder,
        buttonProps:
          defaultDeathOrder === 'primary'
            ? {
                startIcon: CheckIcon,
                iconProps: { size: 16, color: COLORS.TEXT.PRIMARY },
              }
            : {},
      },
      {
        display: 'Secondary individual',
        value: 'secondary' as DefaultDeathOrder,
        buttonProps:
          defaultDeathOrder === 'secondary'
            ? {
                startIcon: CheckIcon,
                iconProps: { size: 16, color: COLORS.TEXT.PRIMARY },
              }
            : {},
      },
    ],
    [defaultDeathOrder]
  );

  useEffect(() => {
    // Set the default relationship type only if the field hasn't been manually changed.
    if (isRelationshipFieldDirty) return;
    setValue(
      'relationshipToBetweenPrimaryClients',
      ClientProfileRelationshipType.Spouse,
      {
        shouldDirty: true,
      }
    );
  }, [setValue, isRelationshipFieldDirty]);

  useEffect(() => {
    if (isClientTwoStateCodeDirty) return;
    if (!clientOneValues?.stateCode) return;

    setValue('clientTwo.stateCode', clientOneValues.stateCode, {
      shouldDirty: false,
    });
  }, [clientOneValues?.stateCode, isClientTwoStateCodeDirty, setValue]);

  // Only living secondary individuals can be selected for death-order modeling.
  useEffect(() => {
    if (isDeathOrderToggleDisabled && defaultDeathOrder === 'secondary') {
      setValue('defaultDeathOrder', 'primary', {
        shouldDirty: false,
      });
    }
  }, [isDeathOrderToggleDisabled, defaultDeathOrder, setValue]);

  return (
    // useFlexGap so the death-order dividers' negative horizontal margin (used
    // to make them full-bleed) isn't wiped by Stack's margin-based spacing.
    <Stack spacing={3} useFlexGap>
      <Stack spacing={2}>
        <Stack spacing={5}>
          <Stack direction="row" spacing={2} alignItems="center">
            <ClientSetupIndividualIndicator variant="primary" />
            <ClientSetupPrimaryClientForm
              required
              fieldNamePrefix="clientOne"
            />
          </Stack>
          <Stack direction="row" spacing={2} alignItems="center">
            <ClientSetupIndividualIndicator variant="secondary" />
            <ClientSetupPrimaryClientForm
              required={clientTwoFieldsFilled}
              fieldNamePrefix="clientTwo"
            />
          </Stack>
        </Stack>
      </Stack>
      {/* Full-bleed dividers (negative margin cancels the body's 24px padding)
          bracket the death-order section, separating it from the client rows
          above and the additional-details card below. */}
      <Divider sx={{ mx: -3, backgroundColor: COLORS.GRAY[200] }} />
      <Stack direction="row" justifyContent="space-between">
        <Stack>
          <Typography variant="title-s">Default death order</Typography>
          <Typography variant="body-xs">
            Luminary uses default death order for assumptions modeling
          </Typography>
        </Stack>
        <ButtonGroup<DefaultDeathOrder>
          label="Default death order"
          hideLabel
          value={defaultDeathOrder}
          onChange={(
            _event: SyntheticEvent<Element, Event>,
            value: DefaultDeathOrder
          ) => setValue('defaultDeathOrder', value)}
          options={deathOrderOptions}
          disabled={isDeathOrderToggleDisabled}
        />
      </Stack>
      <Divider sx={{ mx: -3, backgroundColor: COLORS.GRAY[200] }} />
      <ClientSetupAdditionalDetails />
    </Stack>
  );
}
